import React, { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSidebarContext } from '~/contexts/SidebarContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { bridge } from '~/services/bridge'
import { cm } from '~/utils'

interface ChatDisplayHeaderProps {
  conversationTitle: React.ReactNode
  conversationSubtitle: React.ReactNode
  isSplitPaneLayout?: boolean
  scratchAction?: React.ReactNode
  taskListAction?: React.ReactNode
  useTaskPanelHeaderLayout?: boolean
}

export function ChatDisplayHeader({
  conversationTitle,
  conversationSubtitle,
  isSplitPaneLayout = false,
  scratchAction,
  taskListAction,
  useTaskPanelHeaderLayout = false
}: ChatDisplayHeaderProps) {
  const { collapsed } = useSidebarContext()
  const isPlugin = bridge.isInPlugin()
  const navigate = useNavigate()
  const { workspaceUuid } = useParams<{ workspaceUuid: string }>()
  const { currentWorkspace } = useWorkspaceContext()

  const handleBack = useCallback(() => {
    const targetWorkspace = workspaceUuid ?? currentWorkspace?.uuid
    if (targetWorkspace) {
      navigate(`/w/${targetWorkspace}`)
    } else {
      navigate(-1)
    }
  }, [navigate, workspaceUuid, currentWorkspace?.uuid])

  const conversationIdentity = (
    <div
      className={cm(
        'flex min-w-0 items-center gap-2 transition-transform duration-500 ease-in-out',
        !isPlugin && collapsed
          ? 'translate-x-[132px] -translate-y-px'
          : 'translate-x-0 translate-y-0'
      )}
    >
      <button
        onClick={handleBack}
        className={cm(
          'shrink-0 rounded-md text-tokens-neutral-icon-default transition-colors hover:bg-tokens-neutral-fill-fill-6 hover:text-tokens-neutral-icon-hover',
          isPlugin ? 'p-1' : 'p-1.5'
        )}
      >
        <svg
          className={cm(isPlugin ? 'w-4 h-4' : 'w-5 h-5')}
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={1.5}
            d='M15 19l-7-7 7-7'
          />
        </svg>
      </button>
      <div className='flex min-w-0 flex-col gap-1'>
        {conversationTitle}
        {conversationSubtitle}
      </div>
    </div>
  )

  if (!isPlugin && (isSplitPaneLayout || useTaskPanelHeaderLayout)) {
    return (
      <div className='flex h-[68px] shrink-0 items-center justify-between gap-3 px-6 py-4'>
        {conversationIdentity}
        {scratchAction || taskListAction ? (
          <div className='flex shrink-0 items-center gap-2'>
            {scratchAction}
            {taskListAction}
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div
      className={cm(
        'shrink-0',
        isPlugin
          ? 'sticky top-0 z-10 border-b border-tokens-neutral-border-2 bg-tokens-neutral-bg-white px-3 py-2'
          : 'sticky top-0 z-20 flex h-[52px] items-center gap-0 border-0 border-none border-transparent bg-tokens-neutral-bg-white px-4 py-4'
      )}
    >
      <div className='flex w-full items-center justify-between gap-3'>
        {conversationIdentity}
        {scratchAction || taskListAction ? (
          <div className='flex shrink-0 items-center gap-2'>
            {scratchAction}
            {taskListAction}
          </div>
        ) : null}
      </div>
    </div>
  )
}
